import type {
  GridColDef,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { SOCIETY_SETTING_TYPE } from '@/constants/societySettings';
import {
  AnimalFields,
  FieldOption,
  FieldsWithLink,
  GetAnimalsRow,
  SortDirection,
} from '@/components/publicSearch/constants';
import BreedAverageGridRowLabel from '@/components/publicSearch/animal/ebv/BreedAverageGridRowLabel';
import {
  resolveGridAvgRowCaptions,
  shouldStackBlackRedBreedAverages,
} from '@/components/publicSearch/animal/ebv/breedAverageLabels';
import type {
  AnimalEbvFilteredSortOptionInput,
  AnimalEbvSortField,
  SortDirectionOrder,
} from '@/graphqlClient/types';
import { getGridOperatorsByField } from '@/components/publicSearch/search/helpers';
import { OptionsByField } from '@/components/publicSearch/search/constants';
import { createRenderCell } from './helpers';
import {
  EbvAverageData,
  EbvConfigItem,
  createAverageRow,
  generateEbvColumns,
  BREED_AVERAGE_ROW_ID,
  type EbvTraitHeaderContext,
} from '../ebv/ebvColumns';
import { GetAnimalEbvsQuery } from '@/publicSearch/graphql/queries.generated';
import { trimString } from '@/utils/text';

type AnimalEbvItem = GetAnimalEbvsQuery['animalEbvs']['items'][number];

export type AnimalSearchFieldConfigItem = {
  field: string;
  label: string;
  display: number;
  order: string;
};

/** Parse ANIMAL_LIST_FIELDS from society settings */
export function parseAnimalSearchFieldsConfig(
  settings:
    | Array<{ societySettingType?: string | null; value?: string | null }>
    | null
    | undefined,
): AnimalSearchFieldConfigItem[] {
  if (!settings || !Array.isArray(settings)) return [];
  const setting = settings.find(
    (s) =>
      s && s.societySettingType === SOCIETY_SETTING_TYPE.ANIMAL_LIST_FIELDS,
  );
  if (!setting?.value) return [];
  try {
    const parsed = JSON.parse(setting.value) as AnimalSearchFieldConfigItem[];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item) => item && item.display === 1 && item.field)
      .sort(
        (a, b) => parseInt(String(a.order), 10) - parseInt(String(b.order), 10),
      );
  } catch {
    return [];
  }
}

/** Get visible field keys (row keys) from ANIMAL_LIST_FIELDS config */
export function getVisibleFieldKeysFromAnimalSearchConfig(
  settings:
    | Array<{ societySettingType?: string | null; value?: string | null }>
    | null
    | undefined,
): string[] {
  return parseAnimalSearchFieldsConfig(settings).map((item) =>
    mapSettingFieldToRowKey(item.field),
  );
}

export type AnimalDetailFieldConfigItem = {
  field: string;
  label: string;
  display: number;
  order: string;
};

/** Parse ANIMAL_DETAIL_FIELDS from society settings */
export function parseAnimalDetailFieldsConfig(
  settings:
    | Array<{ societySettingType?: string | null; value?: string | null }>
    | null
    | undefined,
): AnimalDetailFieldConfigItem[] {
  if (!settings || !Array.isArray(settings)) return [];
  const setting = settings.find(
    (s) =>
      s && s.societySettingType === SOCIETY_SETTING_TYPE.ANIMAL_DETAIL_FIELDS,
  );
  if (!setting?.value) return [];
  try {
    const parsed = JSON.parse(setting.value) as AnimalDetailFieldConfigItem[];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (item) =>
          item &&
          item.display === 1 &&
          item.field &&
          typeof item.field === 'string',
      )
      .sort(
        (a, b) => parseInt(String(a.order), 10) - parseInt(String(b.order), 10),
      );
  } catch {
    return [];
  }
}

type BuildAnimalEbvColumnsOptions = {
  orgCode: string;
  ebvConfig: EbvConfigItem[];
  animalSearchFieldsConfig?: AnimalSearchFieldConfigItem[];
  showEBVs: boolean;
  showEBVAccuracy: boolean;
  showPercentiles: boolean;
  /** Symbol used for `$`-format index values (e.g. `R` for South African societies). */
  currencySymbol?: string;
  /** Society value label for EBV column tooltips (EBV / EPD / ABV). */
  ebvLabel?: string;
  onAverageRowClick?: () => void;
  traitHeaderContext?: EbvTraitHeaderContext;
  isBlackRedAngusStrain?: boolean;
  ebvAverage?: EbvAverageData | null;
  showPercentileSearch?: boolean;
  societyId?: string | number;
};

/** Maps society setting field name to row/column key (e.g. SocietyAnimalField8 -> SOCIETY_ANIMAL_FIELD_8) */
export function mapSettingFieldToRowKey(field: string): string {
  const match = field.match(/^SocietyAnimalField(\d+)$/i);
  if (match) {
    return `SOCIETY_ANIMAL_FIELD_${match[1]}`;
  }
  const known: Record<string, string> = {
    AnimalIdent: 'ANIMAL_IDENT',
    AnimalName: 'ANIMAL_NAME',
    Sex: 'SEX',
    DateOfBirth: 'DATE_OF_BIRTH',
    CalvingYear: 'CALVING_YEAR',
    SaleCount: 'SALE_COUNT',
    AnimalStatus: 'ANIMAL_STATUS',
    RegistrationStatus: 'REGISTRATION_STATUS',
    SireIdent: 'SIRE_IDENT',
    DamIdent: 'DAM_IDENT',
    TraitsObserved: 'TRAITS_OBSERVED',
    Breeder: 'BREEDER',
    CurrentOwner: 'CURRENT_OWNER',
    Sire: 'SIRE',
    Dam: 'DAM',
    Tattoo: 'TATTOO',
    // i4-generated ANIMAL_LIST_FIELDS settings emit `AnimalTattoo` (from anim_tattoo),
    // while the row data is keyed `TATTOO`; map both so the column renders.
    AnimalTattoo: 'TATTOO',
    Colour: 'COLOUR',
  };
  return (
    known[field] ||
    field
      .replace(/([A-Z])/g, '_$1')
      .replace(/^_/, '')
      .toUpperCase()
  );
}

export function transformFieldName(field: string) {
  if (field === FieldOption.breeder.value) return 'BREEDER';
  if (field === FieldOption.currentOwner.value) return 'OWNER';
  if (field === FieldOption.dam.value) return 'DAM';
  if (field === FieldOption.sire.value) return 'SIRE';

  // Society custom overflow fields surfaced by ANIMAL_SEARCH_FIELDS keep their setting
  // token (e.g. SocietyAnimalField5); the GraphQL AnimalEbvFilterField enum spells them
  // SOCIETY_ANIMAL_FIELD5 (no underscore before the number).
  const societyAnimalFieldMatch = field.match(/^SocietyAnimalField(\d+)$/i);
  if (societyAnimalFieldMatch) {
    return `SOCIETY_ANIMAL_FIELD${societyAnimalFieldMatch[1]}`;
  }

  const percentileMatch = field.match(/^ebv(\d+)Percentile$/i);
  if (percentileMatch) {
    const number = percentileMatch[1];
    return `EBV${number}_PERCENTILE`;
  }

  const ebvMatch = field.match(/^ebv(\d+)(Acc)?$/i);
  if (ebvMatch) {
    const number = ebvMatch[1];
    const isAcc = ebvMatch[2] !== undefined;
    return `EBV${number}${isAcc ? '_ACCURACY' : ''}`;
  }

  return field;
}

/** Maps DataGrid column field keys to GraphQL AnimalEbvSortField values. */
function mapAnimalEbvGridSortField(field: string): string {
  const gridToSort: Record<string, string> = {
    DAM: 'DAM_NAME',
    SIRE: 'SIRE_NAME',
    BREEDER: 'BREEDER_NAME',
    CURRENT_OWNER: 'CURRENT_OWNER_NAME',
    TATTOO: 'ANIMAL_TATTOO',
  };
  if (gridToSort[field]) {
    return gridToSort[field];
  }

  const societyField = field.match(/^SOCIETY_ANIMAL_FIELD_(\d+)$/);
  if (societyField) {
    return `SOCIETY_ANIMAL_FIELD${societyField[1]}`;
  }

  return field;
}

/**
 * Builds the GraphQL sort options for an animal-EBV query from a MUI grid
 * sort model. EBV value columns sort nulls last; when no sort is active we
 * fall back to descending animal id.
 */
export function buildAnimalEbvSortOptions(
  gridSortModel: GridSortModel,
): AnimalEbvFilteredSortOptionInput[] {
  const options = gridSortModel
    .filter(({ field, sort }) => field && sort)
    .map(({ field, sort }) => {
      const sortField = mapAnimalEbvGridSortField(field as string);
      const isEbvValueSort = /^EBV\d+$/.test(sortField);
      return {
        direction: SortDirection[sort!] as SortDirectionOrder,
        field: sortField as AnimalEbvSortField,
        // Always set: HotChocolate maps C# bool to GraphQL Boolean!, so omitting breaks requests.
        nullsLast: isEbvValueSort,
      };
    });

  if (options.length === 0) {
    options.push({
      field: FieldOption.animalId.value as AnimalEbvSortField,
      direction: SortDirection.desc as SortDirectionOrder,
      nullsLast: false,
    });
  }

  return options;
}

const ANIMAL_NAME_FIELD = FieldOption.animalName.value;
const ON_SALE_FIELD = FieldOption.saleCount.value;

const GRID_SEX_COLUMN_WIDTH_PX = 80;
const GRID_ACTIVE_LISTINGS_MIN_WIDTH_PX = 124;
const GRID_ACTIVE_LISTINGS_WIDTH_PX = 136;
const GRID_ACTIVE_LISTINGS_MAX_WIDTH_PX = 180;

/**
 * Ensures an "Active Listings" column exists immediately after Animal Name,
 * inserting or reordering when society column config omits or places it elsewhere.
 */
function ensureOnSaleColumnNextToAnimalName(
  columns: GridColDef<GetAnimalsRow>[],
  orgCode: string,
): void {
  const nameIdx = columns.findIndex((c) => c.field === ANIMAL_NAME_FIELD);
  if (nameIdx < 0) return;

  const saleIdx = columns.findIndex((c) => c.field === ON_SALE_FIELD);

  if (saleIdx < 0) {
    const renderCell = createRenderCell(
      ON_SALE_FIELD as keyof GetAnimalsRow,
      orgCode,
    );
    columns.splice(nameIdx + 1, 0, {
      field: ON_SALE_FIELD,
      headerName: FieldOption.saleCount.label,
      filterOperators: getGridOperatorsByField(ON_SALE_FIELD),
      headerAlign: 'center',
      align: 'center',
      minWidth: GRID_ACTIVE_LISTINGS_MIN_WIDTH_PX,
      width: GRID_ACTIVE_LISTINGS_WIDTH_PX,
      maxWidth: GRID_ACTIVE_LISTINGS_MAX_WIDTH_PX,
      sortable: true,
      renderCell,
    });
    return;
  }

  if (saleIdx === nameIdx + 1) return;

  const removed = columns.splice(saleIdx, 1);
  const saleCol = removed[0];
  if (!saleCol) return;

  const nameIdxAfter = columns.findIndex((c) => c.field === ANIMAL_NAME_FIELD);
  columns.splice(nameIdxAfter + 1, 0, saleCol);
}

/**
 * Ensures an Animal Name column always exists, inserting a leading one when
 * society column config omits it (e.g. a config whose name column is a
 * SocietyAnimalField "Pedigree Name"). The compact mobile list collapses to
 * this column for its card row, so without it the grid has nothing to show.
 * Default desktop visibility still follows the config, which keeps an
 * injected column hidden until toggled from Manage columns.
 */
function ensureAnimalNameColumn(
  columns: GridColDef<GetAnimalsRow>[],
  orgCode: string,
  averageRowRenderOptions?: Parameters<typeof buildColumnFromConfig>[2],
): void {
  if (columns.some((c) => c.field === ANIMAL_NAME_FIELD)) return;
  const column = buildColumnFromConfig(
    {
      field: 'AnimalName',
      label: FieldOption.animalName.label,
      display: 1,
      order: '0',
    },
    orgCode,
    averageRowRenderOptions,
  );
  if (column) columns.unshift(column);
}

function getColumnMinWidth(fieldValue: string): number {
  if (fieldValue === FieldOption.animalName.value) {
    return 250;
  }
  if (fieldValue === FieldOption.sex.value) {
    return GRID_SEX_COLUMN_WIDTH_PX;
  }
  if (fieldValue === FieldOption.saleCount.value) {
    return GRID_ACTIVE_LISTINGS_MIN_WIDTH_PX;
  }
  return 150;
}

function getColumnWidth(isSex: boolean, isOnSale: boolean): number | undefined {
  if (isSex) {
    return GRID_SEX_COLUMN_WIDTH_PX;
  }
  if (isOnSale) {
    return GRID_ACTIVE_LISTINGS_WIDTH_PX;
  }
  return undefined;
}

function renderBreedAverageNameCell(
  params: GridRenderCellParams<GetAnimalsRow>,
  options: {
    orgCode: string;
    ebvAverage: EbvAverageData | null | undefined;
    isBlackRedAngusStrain: boolean;
    showPercentileSearch: boolean;
    societyId?: string | number;
    onAverageRowClick?: () => void;
  },
) {
  if (!options.ebvAverage) {
    return <span>{params.row.ANIMAL_NAME || ''}</span>;
  }

  return (
    <BreedAverageGridRowLabel
      ebvAverage={options.ebvAverage}
      isBlackRedAngus={options.isBlackRedAngusStrain}
      showPercentileSearch={options.showPercentileSearch}
      orgCode={options.orgCode}
      societyId={options.societyId}
      onAverageRowClick={options.onAverageRowClick}
    />
  );
}

function buildColumnFromConfig(
  configItem: AnimalSearchFieldConfigItem,
  orgCode: string,
  averageRowRenderOptions?: {
    ebvAverage: EbvAverageData | null | undefined;
    isBlackRedAngusStrain: boolean;
    showPercentileSearch: boolean;
    societyId?: string | number;
    onAverageRowClick?: () => void;
  },
): GridColDef<GetAnimalsRow> | null {
  // Skip items with empty or invalid field names
  if (!configItem.field || typeof configItem.field !== 'string') {
    return null;
  }
  const fieldKey = mapSettingFieldToRowKey(configItem.field);
  const label = (configItem.label || '').replace(/\^/g, ' ');
  const isOnSaleCol = fieldKey === FieldOption.saleCount.value;
  const headerName = isOnSaleCol ? FieldOption.saleCount.label : label;
  let renderCell = createRenderCell(fieldKey as keyof GetAnimalsRow, orgCode);

  if (fieldKey === FieldOption.animalName.value) {
    renderCell = (params: GridRenderCellParams<GetAnimalsRow>) => {
      if (params.row.ID === BREED_AVERAGE_ROW_ID) {
        if (averageRowRenderOptions) {
          return renderBreedAverageNameCell(params, {
            orgCode,
            ...averageRowRenderOptions,
          });
        }
        return <span>{params.row.ANIMAL_NAME || ''}</span>;
      }
      return createRenderCell(fieldKey as keyof GetAnimalsRow, orgCode)(params);
    };
  }

  const isSex = fieldKey === FieldOption.sex.value;
  const nameValueFormatter =
    fieldKey === FieldOption.animalName.value
      ? (value: string | null | undefined) =>
          value == null || value === '' ? '' : String(value)
      : undefined;
  return {
    field: fieldKey,
    filterOperators: getGridOperatorsByField(fieldKey),
    flex: isSex || isOnSaleCol ? undefined : 1,
    headerClassName: FieldsWithLink.includes(fieldKey) ? 'pl' : '',
    headerName,
    align: isOnSaleCol ? 'center' : undefined,
    headerAlign: isOnSaleCol ? 'center' : undefined,
    minWidth: getColumnMinWidth(fieldKey),
    sortable: true,
    type: fieldKey in OptionsByField ? 'singleSelect' : undefined,
    width: getColumnWidth(isSex, isOnSaleCol),
    renderCell,
    ...(nameValueFormatter ? { valueFormatter: nameValueFormatter } : {}),
    valueOptions:
      fieldKey in OptionsByField
        ? () => OptionsByField[fieldKey]!.map(({ value }) => value)
        : undefined,
  };
}

export function buildAnimalEbvColumns({
  orgCode,
  ebvConfig,
  animalSearchFieldsConfig = [],
  showEBVs,
  showEBVAccuracy,
  showPercentiles,
  currencySymbol,
  ebvLabel = 'EBV',
  onAverageRowClick,
  traitHeaderContext,
  isBlackRedAngusStrain = false,
  ebvAverage = null,
  showPercentileSearch = false,
  societyId,
}: BuildAnimalEbvColumnsOptions): GridColDef<GetAnimalsRow>[] {
  const averageRowRenderOptions =
    ebvAverage != null
      ? {
          ebvAverage,
          isBlackRedAngusStrain,
          showPercentileSearch,
          societyId,
          onAverageRowClick,
        }
      : undefined;

  // Filter to only valid config items (non-empty field names)
  const validConfig = animalSearchFieldsConfig.filter(
    (item) => item && item.field && typeof item.field === 'string',
  );

  const columns: GridColDef<GetAnimalsRow>[] =
    validConfig.length > 0
      ? validConfig
          .map((item) =>
            buildColumnFromConfig(item, orgCode, averageRowRenderOptions),
          )
          .filter((col): col is GridColDef<GetAnimalsRow> => col !== null)
      : AnimalFields.map(({ label, value }) => {
          let renderCell = createRenderCell(
            value as keyof GetAnimalsRow,
            orgCode,
          );

          if (value === FieldOption.animalName.value) {
            renderCell = (params: GridRenderCellParams<GetAnimalsRow>) => {
              if (params.row.ID === BREED_AVERAGE_ROW_ID) {
                if (averageRowRenderOptions) {
                  return renderBreedAverageNameCell(params, {
                    orgCode,
                    ...averageRowRenderOptions,
                  });
                }
                return <span>{params.row.ANIMAL_NAME || ''}</span>;
              }
              return createRenderCell(
                value as keyof GetAnimalsRow,
                orgCode,
              )(params);
            };
          }

          const isSex = value === FieldOption.sex.value;
          const isOnSale = value === FieldOption.saleCount.value;
          const nameValueFormatter =
            value === FieldOption.animalName.value
              ? (v: string | null | undefined) =>
                  v == null || v === '' ? '' : String(v)
              : undefined;
          return {
            field: value,
            filterOperators: getGridOperatorsByField(value),
            flex: isSex || isOnSale ? undefined : 1,
            headerClassName: FieldsWithLink.includes(value) ? 'pl' : '',
            headerName: label,
            align: isOnSale ? 'center' : undefined,
            headerAlign: isOnSale ? 'center' : undefined,
            minWidth: getColumnMinWidth(value),
            sortable: true,
            type: value in OptionsByField ? 'singleSelect' : undefined,
            width: getColumnWidth(isSex, isOnSale),
            renderCell,
            ...(nameValueFormatter
              ? { valueFormatter: nameValueFormatter }
              : {}),
            valueOptions:
              value in OptionsByField
                ? () => OptionsByField[value]!.map(({ value }) => value)
                : undefined,
          };
        });

  ensureAnimalNameColumn(columns, orgCode, averageRowRenderOptions);
  ensureOnSaleColumnNextToAnimalName(columns, orgCode);

  if (ebvConfig.length > 0) {
    const ebvColumns = generateEbvColumns<GetAnimalsRow>(
      ebvConfig,
      showEBVs,
      showEBVAccuracy,
      showPercentiles,
      currencySymbol,
      ebvLabel,
      traitHeaderContext,
      { isBlackRedAngusStrain, ebvAverage },
    );
    columns.push(...ebvColumns);
  }

  return columns;
}

type BuildAnimalEbvRowsOptions = {
  items?: AnimalEbvItem[];
  ebvConfig: EbvConfigItem[];
  ebvAverage: EbvAverageData | null;
  showEBVs: boolean;
  showEBVAccuracy: boolean;
  showPercentiles: boolean;
  isCardView: boolean;
  /** Symbol used for `$`-format index values (e.g. `R` for South African societies). */
  currencySymbol?: string;
  /** Override label for the breed-average summary row (animal name column). */
  averageRowLabel?: string;
  isBlackRedAngusStrain?: boolean;
};

function buildAnimalEbvRows({
  items,
  ebvConfig,
  ebvAverage,
  showEBVs,
  showEBVAccuracy,
  showPercentiles,
  isCardView,
  currencySymbol,
  averageRowLabel,
  isBlackRedAngusStrain = false,
}: BuildAnimalEbvRowsOptions): GetAnimalsRow[] {
  const dataRows =
    items?.map((item) => {
      const row = { ID: item.animalId } as GetAnimalsRow;
      const rowAny = row as Record<string, unknown>;
      row.TRAIT_LEADER = item.traitLeader || '';
      if (item.documents?.length) {
        rowAny['documents'] = item.documents;
      }
      rowAny['SALE_COUNT'] =
        typeof item.saleCount === 'number' ? item.saleCount : 0;

      for (let i = 1; i <= 26; i++) {
        const ebvKey = `ebv${i}` as keyof typeof item;
        const ebvAccKey = `ebv${i}Accuracy` as keyof typeof item;
        const ebvPercentileKey = `ebv${i}Percentile` as keyof typeof item;

        if (
          ebvKey in item &&
          item[ebvKey] !== null &&
          item[ebvKey] !== undefined
        ) {
          const ebvField = `EBV${i}`;
          // Keep numeric 0: `|| ''` blanked genuine zero EBVs (BLS-387).
          rowAny[ebvField] = item[ebvKey];
        }

        if (
          ebvAccKey in item &&
          item[ebvAccKey] !== null &&
          item[ebvAccKey] !== undefined
        ) {
          const accField = `EBV${i}_ACC`;
          rowAny[accField] = item[ebvAccKey];
        }

        if (
          ebvPercentileKey in item &&
          item[ebvPercentileKey] !== null &&
          item[ebvPercentileKey] !== undefined
        ) {
          const percentileField = `EBV${i}_PERCENTILE`;
          rowAny[percentileField] = item[ebvPercentileKey];
        }
      }

      if (item.breederId || item.breederName) {
        row.BREEDER = {
          id: item.breederId || 0,
          value: item.breederName || '',
          detailsAvailFlag: item.breederDetailsAvailFlag ?? null,
        };
      }
      if (item.currentOwnerId || item.currentOwnerName) {
        row.CURRENT_OWNER = {
          id: item.currentOwnerId || 0,
          value: item.currentOwnerName || '',
          detailsAvailFlag: item.currentOwnerDetailsAvailFlag ?? null,
        };
      }
      if (item.sireId || item.sireName) {
        row.SIRE = {
          id: item.sireId || 0,
          value: item.sireName || '',
        };
      }
      if (item.damId || item.damName) {
        row.DAM = {
          id: item.damId || 0,
          value: item.damName || '',
        };
      }

      // Map sireIdent and damIdent for SireIdent/DamIdent columns
      const itemAny = item as Record<string, unknown>;
      if (itemAny.sireIdent != null && itemAny.sireIdent !== '') {
        (row as Record<string, unknown>)['SIRE_IDENT'] = itemAny.sireIdent;
      }
      if (itemAny.damIdent != null && itemAny.damIdent !== '') {
        (row as Record<string, unknown>)['DAM_IDENT'] = itemAny.damIdent;
      }

      // The GraphQL field is `animalTattoo` but FieldOption.tattoo.key is `tattoo`,
      // so the AnimalFields lookup in the loop below never matches and the
      // `case 'animalTattoo'` there is dead code. Populate TATTOO explicitly.
      if (itemAny.animalTattoo != null && itemAny.animalTattoo !== '') {
        row.TATTOO = itemAny.animalTattoo as string;
      }

      // Map societyAnimalField1-30 and traitsObserved
      for (let i = 1; i <= 30; i++) {
        const key = `societyAnimalField${i}`;
        if (key in item && itemAny[key] != null && itemAny[key] !== '') {
          (row as Record<string, unknown>)[`SOCIETY_ANIMAL_FIELD_${i}`] =
            itemAny[key];
        }
      }
      if (itemAny.traitsObserved != null && itemAny.traitsObserved !== '') {
        (row as Record<string, unknown>)['TRAITS_OBSERVED'] =
          itemAny.traitsObserved;
      }

      for (const key in item) {
        const option = AnimalFields.find((field) => field.key === key);
        const value = option?.value as keyof GetAnimalsRow;

        if (!option || !value) continue;

        switch (key) {
          case FieldOption.animalName.key:
            if (!item.animalId && !item.animalName) continue;
            row.ANIMAL_NAME = item.animalName || '';
            break;
          case 'animalTattoo':
            row.TATTOO = (itemAny[key] as string) || '';
            break;
          case FieldOption.sex.key:
            row.SEX = (itemAny[key] as string) ?? '';
            break;
          case FieldOption.registrationStatus.key:
            row.REGISTRATION_STATUS = trimString(itemAny[key] as string);
            break;
          default:
            rowAny[value as string] = itemAny[key];
            break;
        }
      }

      return row;
    }) || [];

  const averageRow =
    !isCardView &&
    (showEBVs || showEBVAccuracy || showPercentiles) &&
    ebvAverage &&
    ebvConfig.length > 0
      ? createAverageRow<GetAnimalsRow>(
          ebvAverage,
          ebvConfig,
          currencySymbol,
          averageRowLabel,
          {
            isBlackRedAngusStrain: shouldStackBlackRedBreedAverages(
              isBlackRedAngusStrain,
              ebvAverage,
            ),
          },
        )
      : null;

  return averageRow ? [averageRow, ...dataRows] : dataRows;
}

type BuildAnimalEbvRowsWithAverageLabelOptions = Omit<
  BuildAnimalEbvRowsOptions,
  'averageRowLabel'
> & {
  isBlackRedAngus: boolean;
  showPercentileSearch: boolean;
};

/**
 * Wraps {@link buildAnimalEbvRows}, deriving the breed-average summary-row
 * label from setting 15 captions before building the rows.
 */
export function buildAnimalEbvRowsWithAverageLabel({
  isBlackRedAngus,
  showPercentileSearch,
  ebvAverage,
  ...rest
}: BuildAnimalEbvRowsWithAverageLabelOptions): GetAnimalsRow[] {
  const averageRowLabel = ebvAverage
    ? resolveGridAvgRowCaptions(
        shouldStackBlackRedBreedAverages(isBlackRedAngus, ebvAverage),
        ebvAverage,
        showPercentileSearch,
      )
    : undefined;

  return buildAnimalEbvRows({
    ...rest,
    ebvAverage,
    averageRowLabel,
    isBlackRedAngusStrain: isBlackRedAngus,
  });
}
